import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { disableSendDefaultMessage, setEditableMessage } from '../../actions/chat/actions';
import { Captcha } from '../../components/Captcha';
import { MessageInputField } from '../../components/MessageInputField';
import { ReplyTemplateButton } from '../../components/ReplyTemplateButton';
import { BLOB_ID } from '../../constants/blobId';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { useReplyTemplateModal } from '../../hooks';
import { useSendMessage } from '../../hooks/useSendMessage/useSendMessage';
import {
  chatbotSelector,
  chatListSelector,
  chatWindowSelector,
  currentChatOpponentIdSelector,
  currentOpenChatSelector,
  hostTypeSelector,
  isShowSubAgentChatListSelector,
  offerSelector,
} from '../../selectors';
import { hintQuestionsIsVisibleSelector } from '../../selectors/hintQuestionsSelector';
import { EAvailableActions } from '../../types/availableActions';
import { EAnalyticsKey } from '../../types/chatHint';
import { EFirstMessageStatus } from '../../types/firstMessageStatus';
import { EHostType } from '../../types/hostType';
import { ERoute } from '../../types/route';
import { useApplicationContext } from '../../utils/applicationContext';
import { isFileUploadButtonEnabled } from '../../utils/isFileUploadButtonEnabled';
import { AttachFileButtonContainer } from '../AttachFileButtonContainer';
import { ChatbotMenuContainer } from '../ChatbotMenuContainer';
import { HintQuestionsContainer } from '../HintQuestionsContainer';
import { EReplyTemplateTrackingSource, trackReplyTemplateButtonClick } from '../ReplyTemplateModalContainer/tracking';

interface IMessageInputFieldContainerProps {
  onHeightChange(): void;
}

export const MessageInputFieldContainer: React.FC<IMessageInputFieldContainerProps> = ({
  onHeightChange,
}: IMessageInputFieldContainerProps) => {
  const dispatch = useDispatch();
  const offer = useSelector(offerSelector);
  const currentChat = useSelector(currentOpenChatSelector);
  const hostType = useSelector(hostTypeSelector);
  const { isBubbleInputFocused } = useSelector(chatWindowSelector);
  const isShowSubAgentChatList = useSelector(isShowSubAgentChatListSelector);
  const chatList = useSelector(chatListSelector);
  const hintQuestionsIsVisible = useSelector(hintQuestionsIsVisibleSelector);
  const { isTyping, messageSending: chatbotMessageSending } = useSelector(chatbotSelector);
  const opponentId = useSelector(currentChatOpponentIdSelector);

  const { config } = useApplicationContext();

  const { chatId, availableActions, firstMessageStatus, editableMessage, sendDefaultMessage, defaultMessagePayload } =
    currentChat || {};
  const { id: offerId } = offer || {};
  const { onSend, setAnalyticsKey } = useSendMessage();
  const {
    isReplyTemplateEnabled,
    isReplyTemplateNewEnabledInputButton,
    isReplyTemplateInputButtonVisible,
    openReplyTemplateModal,
  } = useReplyTemplateModal();

  const isChatbotGPTEnabled = config.get<boolean>('frontend-chats.chatbotGPT.Enabled');

  const isIframe = hostType === EHostType.Frame;

  const isAttachButtonEnabled =
    isFileUploadButtonEnabled(config) && availableActions?.includes(EAvailableActions.SendFile);
  const isSendMessageEnabled =
    (isIframe && hintQuestionsIsVisible) ||
    (isIframe && typeof availableActions === 'undefined') ||
    (isIframe && availableActions === null) ||
    availableActions?.includes(EAvailableActions.SendMessage) ||
    chatId === CHATBOT_ID ||
    defaultMessagePayload;
  const isShowCaptcha = firstMessageStatus === EFirstMessageStatus.NeedCaptcha;

  const onHintQuestionChange = React.useCallback(
    (analyticsKey: EAnalyticsKey) => {
      setAnalyticsKey(analyticsKey);
    },
    [setAnalyticsKey],
  );

  const handleReplyTemplateClick = React.useCallback(() => {
    trackReplyTemplateButtonClick({ chatId, opponentId, source: EReplyTemplateTrackingSource.TextField });
    openReplyTemplateModal(EReplyTemplateTrackingSource.TextField);
  }, [chatId, openReplyTemplateModal, opponentId]);

  const leftButton = React.useMemo(() => {
    if (chatId === CHATBOT_ID && isChatbotGPTEnabled) {
      return <ChatbotMenuContainer />;
    }

    return isAttachButtonEnabled ? <AttachFileButtonContainer /> : null;
  }, [isAttachButtonEnabled, chatId, isChatbotGPTEnabled]);

  const replyTemplateButton = React.useMemo(
    () =>
      isReplyTemplateEnabled && isReplyTemplateInputButtonVisible ? (
        <ReplyTemplateButton isNewEnabled={isReplyTemplateNewEnabledInputButton} onClick={handleReplyTemplateClick} />
      ) : null,
    [
      isReplyTemplateEnabled,
      isReplyTemplateInputButtonVisible,
      isReplyTemplateNewEnabledInputButton,
      handleReplyTemplateClick,
    ],
  );

  React.useEffect(() => {
    if (editableMessage && sendDefaultMessage && chatId) {
      dispatch(disableSendDefaultMessage({ chatId }));
      if (chatId !== BLOB_ID) {
        dispatch(setEditableMessage({ chatId, text: '' }));
      }
      onSend({ text: editableMessage, chatId, payload: defaultMessagePayload });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, offerId, editableMessage, sendDefaultMessage]);

  if (
    !chatId ||
    isBubbleInputFocused ||
    chatList.currentRoute === ERoute.Spam ||
    (chatId === CHATBOT_ID && !isChatbotGPTEnabled)
  ) {
    return null;
  }

  return (
    <MessageInputField
      hintQuestions={<HintQuestionsContainer onHintQuestionChange={onHintQuestionChange} />}
      chatId={chatId}
      isLoading={firstMessageStatus === EFirstMessageStatus.Pending || chatbotMessageSending}
      isInputDisabled={!isSendMessageEnabled}
      isSendButtonDisabled={!isSendMessageEnabled || isTyping}
      onSend={onSend}
      leftButton={leftButton}
      captcha={isShowCaptcha && <Captcha chatId={chatId} />}
      onHeightChange={onHeightChange}
      placeholder={
        isShowSubAgentChatList
          ? 'Написать сообщение от лица агента'
          : chatId === CHATBOT_ID && isChatbotGPTEnabled
            ? 'Напишите, что вы ищете'
            : ''
      }
      replyTemplateButton={replyTemplateButton}
    />
  );
};
